// 2P AI — dettaglio progetto (§8.4): briciole, intestazione, gruppi Attive/Concluse.
import { useLocation } from 'wouter';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ago } from '../lib/time';
import { useProjects, useSessions, useServers, useCloseSession } from '../lib/queries';
import { isConcluded, isOpen } from '../lib/sessions';
import { CommandBar } from '../components/CommandBar';
import { Crumb } from '../components/Crumb';
import { Icon } from '../components/Icon';
import { SessionRow } from '../components/SessionRow';

interface CcSession {
  cc_session_id: string;
  title: string;
  modified_at: string;
  size: number;
  in_plancia: boolean;
  plancia_session_id: string | null;
  plancia_status: string | null;
}

export function ProjectDetail({ id }: { id: string }) {
  const [, navigate] = useLocation();
  const projects = useProjects().data ?? [];
  const sessions = useSessions().data ?? [];
  const servers = useServers().data ?? [];
  const { closeOne, closeMany, deleteOne, deleteMany } = useCloseSession();

  const project = projects.find((p) => p.id === id);
  const own = sessions.filter((s) => s.project_id === id);
  const active = own.filter((s) => isOpen(s.status));
  const concluded = own.filter((s) => isConcluded(s.status));
  const awaiting = own.filter((s) => s.status === 'awaiting_approval').length;
  const serverName = project ? servers.find((s) => s.id === project.server_id)?.name ?? project.server_id : '';

  const closeActive = () => closeMany(active, `Chiudere ${active.length} sessioni attive di questo progetto?`);

  const cc = useQuery({
    queryKey: ['cc-sessions', id],
    queryFn: () => api.get<{ items: CcSession[] }>(`/projects/${id}/cc-sessions`).then((d) => d.items),
    retry: false,
  }).data ?? [];
  const [adopting, setAdopting] = useState<string | null>(null);
  const [ccErr, setCcErr] = useState<string | null>(null);

  async function openCc(s: CcSession) {
    setCcErr(null);
    if (s.in_plancia && s.plancia_session_id) {
      navigate(`/s/${s.plancia_session_id}`);
      return;
    }
    setAdopting(s.cc_session_id);
    try {
      const res = await api.post<{ session: { id: string } }>(`/projects/${id}/cc-sessions/adopt`, { cc_session_id: s.cc_session_id, title: s.title });
      navigate(`/s/${res.session.id}`);
    } catch (e) {
      setCcErr(e instanceof Error ? e.message : 'Impossibile aprire la sessione');
    } finally {
      setAdopting(null);
    }
  }

  return (
    <div className="app">
      <CommandBar onSettings={() => navigate('/settings')} />
      <Crumb current={project?.name ?? '…'} onBack={() => navigate('/')} />

      {!project ? (
        <p className="scr-sub">Progetto non trovato.</p>
      ) : (
        <>
          <section className="phead">
            <div className="pinfo">
              <div className="ptitle"><span className="name">{project.name}</span></div>
              <div className="pmeta">
                <span className="srv"><Icon name="dns" />{serverName}</span>
                <span className="pathtag"><Icon name="folder" />{project.working_dir}</span>
              </div>
              <div className="pstats">
                <span className="hi">{active.length} {active.length === 1 ? 'attiva' : 'attive'}</span>
                {awaiting > 0 ? ` · ${awaiting} attende il tuo OK` : ''} · {concluded.length} concluse
              </div>
            </div>
            <div className="actions" style={{ display: 'flex', gap: 10 }}>
              <button className="iconbtn" type="button" title="Configura progetto" aria-label="Configura progetto" onClick={() => navigate(`/p/${id}/config`)}><Icon name="settings" /></button>
              <button className="btn-primary" type="button" onClick={() => navigate(`/p/${id}/new`)}><Icon name="add" />Nuova sessione</button>
            </div>
          </section>

          <div className="ghead">
            <div className="lhs"><h2>Attive</h2><span className="badge">{active.length}</span></div>
            {active.length > 0 ? (
              <button className="btn-close-all" type="button" title="Chiudi le sessioni attive del progetto" onClick={() => void closeActive()}>
                <Icon name="stop_circle" />Chiudi le attive
              </button>
            ) : null}
          </div>
          {active.length === 0 ? (
            <p className="scr-sub">Nessuna sessione attiva.</p>
          ) : (
            <section className="list" aria-label="Sessioni attive">
              {active.map((s) => (
                <SessionRow key={s.id} session={s} onOpen={() => navigate(`/s/${s.id}`)} onClose={() => void closeOne(s)} showClose />
              ))}
            </section>
          )}

          <div className="ghead">
            <div className="lhs"><h2>Concluse</h2><span className="badge">{concluded.length}</span></div>
            {concluded.length > 0 ? (
              <button className="btn-close-all" type="button" title="Elimina tutte le sessioni concluse" onClick={() => void deleteMany(concluded, `Eliminare ${concluded.length} sessioni concluse e il loro storico? Non si può annullare.`)}>
                <Icon name="delete_sweep" />Svuota concluse
              </button>
            ) : null}
          </div>
          {concluded.length === 0 ? (
            <p className="scr-sub">Nessuna sessione conclusa.</p>
          ) : (
            <section className="list" aria-label="Sessioni concluse">
              {concluded.map((s) => (
                <SessionRow key={s.id} session={s} onOpen={() => navigate(`/s/${s.id}`)} onDelete={() => void deleteOne(s)} />
              ))}
            </section>
          )}

          <div className="ghead"><div className="lhs"><h2>Cronologia Claude Code</h2><span className="badge">{cc.length}</span></div></div>
          <p className="scr-sub" style={{ marginTop: -2 }}>Tutte le sessioni di Claude Code in questa cartella, <b>incluse quelle aperte dal terminale</b>. Aprine una per riprenderla qui.</p>
          {ccErr ? <div className="note danger"><Icon name="error" /><span>{ccErr}</span></div> : null}
          {cc.length === 0 ? (
            <p className="scr-sub">Nessuna sessione di Claude Code trovata.</p>
          ) : (
            <section className="list" aria-label="Cronologia Claude Code">
              {cc.map((s) => (
                <button key={s.cc_session_id} type="button" className="ccrow" disabled={adopting === s.cc_session_id} onClick={() => void openCc(s)}>
                  <Icon name="terminal" className="cci" />
                  <span className="ccm">
                    <span className="cct">{s.title}</span>
                    <span className="ccs"><span className="ccid">{s.cc_session_id.slice(0, 8)}</span> · {ago(s.modified_at)} <span className={s.in_plancia ? 'ccbadge p' : 'ccbadge'}>{s.in_plancia ? '2P AI' : 'CLI'}</span></span>
                  </span>
                  <Icon name={adopting === s.cc_session_id ? 'hourglass_top' : s.in_plancia ? 'open_in_new' : 'play_arrow'} className="ccgo" />
                </button>
              ))}
            </section>
          )}
        </>
      )}

      <div className="footer">2P AI · CRM per Claude Code</div>
    </div>
  );
}
